import { AnalysisRun, AnalysisRunService } from "./analysis-run.service";
import { officeCall, AnalysisError } from "../utils/analysis-async";
import { EvaluationFlowsEnum } from "../models/shared.model";
import { Inject, Injectable, Injector } from "@angular/core";
import { CacheNamesEnum, DangerousExtensionDTO, IEvaluationStepsConfig } from "../models/shared.model";
import { WaitService } from "./wait.service";
import { IEvaluator } from "../interfaces/evaluator-interface";
import { Evaluator } from "../evaluators/evaluator";
import AttachmentDetails = Office.AttachmentDetails;

@Injectable()
export class AttachmentService {
  private attachments: Office.AttachmentDetails[] = [];

  constructor(
    private injector: Injector,
    private runs: AnalysisRunService,
    @Inject(WaitService) private waitService: WaitService,
  ) {
    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this.attachments = [];
      }
    });
  }

  public attachmentsFlow(enumValue: string, flow: IEvaluationStepsConfig, run: AnalysisRun = this.runs.current) {
    run.assertActive();
    let attachments = run.item.attachments || [];
    let temp: Array<IEvaluator> = [];

    attachments.forEach(value => {
      if (!value.isInline) {
        const ev = new Evaluator(this.injector, enumValue, flow, value);
        temp.push(ev);
      }
    });

    return temp;
  }

  getAttachments() {
    if (Office.context.mailbox.item != null && this.attachments.length == 0) {
      this.attachments = Office.context.mailbox.item.attachments;
      return this.attachments;
    }

    return this.attachments;
  }

  parseAttachmentExtension(attachment: Office.AttachmentDetails) {
    return attachment.name.split(".").pop().toLowerCase();
  }

  getDangerousFileExtensions(): DangerousExtensionDTO[] {

    let list = JSON.parse(window.localStorage.getItem(CacheNamesEnum.DANGEROUS_FILE_EXTENSIONS)!) as DangerousExtensionDTO[];

    if (!list || list.length == 0) {
      list = [
        {
          name: "exe",
          level: 2
        },
        {
          name: "js",
          level: 0
        },
        {
          name: "pdf",
          level: 0
        },
        {
          name: "vbs",
          level: 0
        },
        {
          name: "doc",
          level: 1
        },
        {
          name: "docm",
          level: 1
        },
        {
          name: "xls",
          level: 1
        },
        {
          name: "xlsm",
          level: 1
        },
        {
          name: "ppt",
          level: 1
        },
        {
          name: "pptm",
          level: 1
        },
        {
          name: "zip",
          level: 0
        },
        {
          name: "rar",
          level: 0
        },
        {
          name: "php",
          level: 2
        },
        {
          name: "asp",
          level: 2
        },
        {
          name: "cmd",
          level: 2
        },
        {
          name: "vbscript",
          level: 2
        },
        {
          name: "html",
          level: 2
        },
        {
          name: "bat",
          level: 2
        },
        {
          name: "msc",
          level: 2
        },
        {
          name: "sh",
          level: 2
        },
        {
          name: "jar",
          level: 1
        },
        {
          name: "ps1",
          level: 1
        },
        {
          name: "lnk",
          level: 1
        }
      ];

      list.forEach(e => e.name = e.name.toLowerCase());

      window.localStorage.setItem(CacheNamesEnum.DANGEROUS_FILE_EXTENSIONS, JSON.stringify(list));
    }

    return list;
  }

  async loadImagesFromAttachments(imageExtensions: string[], run: AnalysisRun = this.runs.current): Promise<Blob[]> {
    run.assertActive();
    if (!run.item) throw new AnalysisError('unavailable', 'No message selected');
    const images: Blob[] = [];
    const attachments = (run.item.attachments || []).filter(value =>
      imageExtensions.includes(this.parseAttachmentExtension(value)));
    for (const attachment of attachments) {
      run.assertActive();
      try { images.push(await this.getAttachmentContent(attachment, run)); }
      catch (error) {
        run.assertActive();
        run.issue(EvaluationFlowsEnum.QRCODE, error, `attachment:${attachment.id}`);
      }
    }
    return images;
  }

  async getAttachmentContent(attachment: AttachmentDetails, run: AnalysisRun = this.runs.current): Promise<Blob> {
    run.assertActive();
    if (!run.item) throw new AnalysisError('unavailable', 'No message selected');
    const result = await officeCall<Office.AttachmentContent>(callback =>
      run.item.getAttachmentContentAsync(attachment.id, callback), 'Attachment content', run);
    run.assertActive();
    if (result.format !== Office.MailboxEnums.AttachmentContentFormat.Base64) {
      throw new AnalysisError('unavailable', 'Attachment is not available as base64');
    }
    const bytes = Uint8Array.from(atob(result.content), character => character.charCodeAt(0));
    return new Blob([bytes], { type: attachment.contentType });
  }
}
